import { toast } from 'sonner';
import { useUserList } from '../../api/users';
import {
  useUpdateSetting,
  SETTING_KEYS,
  type SystemSetting,
} from '../../api/settings';

interface BedarfUpdateRecipientsCardProps {
  setting: SystemSetting | undefined;
  formatDateTime: (value: string | null | undefined) => string;
}

/** Nur ein Array von Strings ist eine gültige Empfängerliste — alles andere zählt als leer. */
function readRecipientIds(setting: SystemSetting | undefined): string[] {
  const value = setting?.value;
  if (!Array.isArray(value)) return [];
  return value.filter((entry): entry is string => typeof entry === 'string');
}

/**
 * Wer bei Änderungen an Schule oder Stundenplan eines Bedarfs eine E-Mail bekommt.
 *
 * Eine eigene Karte statt eines weiteren Eintrags in `KNOWN_SETTINGS`: das dort ist
 * eine Liste von Schaltern, und diese Einstellung ist eine Auswahl. Die Optik bleibt
 * dieselbe, das Speichern auch — jeder Klick geht sofort raus, die Seite hat bewusst
 * keinen Speichern-Knopf.
 */
export function BedarfUpdateRecipientsCard({
  setting,
  formatDateTime,
}: BedarfUpdateRecipientsCardProps) {
  const { data, isLoading } = useUserList({ isActive: true, limit: 100 });
  const updateMutation = useUpdateSetting();

  const users = data?.data ?? [];
  const selected = readRecipientIds(setting);

  const handleToggle = async (userId: string) => {
    const next = selected.includes(userId)
      ? selected.filter((id) => id !== userId)
      : [...selected, userId];

    try {
      await updateMutation.mutateAsync({
        key: SETTING_KEYS.BEDARF_UPDATE_RECIPIENTS,
        value: next,
      });
      toast.success('Empfänger aktualisiert');
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Aktualisierung fehlgeschlagen';
      toast.error(message);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
      <h2 className="text-base font-semibold text-gray-900">Bedarf-Updates</h2>
      <p className="mt-1 text-sm text-gray-600 leading-relaxed">
        Die hier ausgewählten Benutzer bekommen sofort eine E-Mail, sobald sich an einem
        Bedarf die Schule, die Schuladresse oder der Stundenplan ändert — auch dann, wenn
        ein bislang leerer Stundenplan erstmals eingetragen wird. Andere Änderungen, etwa
        am Status, lösen nichts aus. Wer eine Änderung selbst speichert, bekommt darüber
        keine E-Mail.
      </p>

      <div className="mt-4">
        {isLoading ? (
          <p className="text-sm text-gray-500">Lade Benutzer…</p>
        ) : users.length === 0 ? (
          <p className="text-sm text-gray-500">Keine aktiven Benutzer gefunden.</p>
        ) : (
          <div className="divide-y divide-gray-100 border border-gray-200 rounded-md">
            {users.map((user) => {
              const checked = selected.includes(user.id);
              return (
                <label
                  key={user.id}
                  className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-gray-50"
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    checked={checked}
                    onChange={() => handleToggle(user.id)}
                    disabled={updateMutation.isPending}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-gray-900 truncate">
                      {user.firstName} {user.lastName}
                    </span>
                    <span className="block text-xs text-gray-500 truncate">
                      {user.email}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
        )}
      </div>

      {selected.length === 0 && !isLoading && (
        <p className="mt-3 text-sm text-amber-600">
          Derzeit wird niemand über Bedarf-Änderungen benachrichtigt.
        </p>
      )}

      {setting && (
        <div className="mt-4 pt-3 border-t border-gray-100 text-xs text-gray-500">
          Zuletzt geändert{' '}
          {setting.updatedBy
            ? `von ${setting.updatedBy.firstName} ${setting.updatedBy.lastName}`
            : ''}{' '}
          am {formatDateTime(setting.updatedAt)}
        </div>
      )}
    </div>
  );
}
